import cx from 'classnames';
import { Location } from 'history';
import {
    AUTOMATIC_COMMENT_VIOLATES_RULES,
    DELETE,
    FORUM_COMMENT_EDIT_MENU,
    FORUM_COMMENT_MOVE_UP,
    SEND_HEART_TO_username,
    SHOW_USERS_WHO_LIKED_THIS_POST,
} from 'mk/autogenerated/translations/CommentMenu.3c70c81e1bf3b88338cd0caa3ab34e61'
import { isAnonymousUser, isAuthenticated } from 'mk/bazaar/common/userUtils';
import { ACTIVE_SERVER_ID } from 'mk/settings';
import { BLOG_POST_COMMENT_MENU_MORE } from 'mk2/apps/blogs/helpers';
import { CommentEntity } from 'mk2/apps/blogs/schemas';
import PostMenuTrigger from 'mk2/apps/forum/components/PostMenuTrigger';
import { FORUM_COMMENT_MENU_MORE } from 'mk2/apps/forum/helpers';
import { forumCommentEditUrl, forumPostCommentLikersUrl, forumPostDetailUrl } from 'mk2/apps/forum/urls';
import { groupsEntityLikersUrl } from 'mk2/apps/groups/urls';
import { heartsUrl } from 'mk2/apps/hearts/urls';
import MenuItem from 'mk2/components/menu/MenuItem';
import { ComplaintTargetType } from 'mk2/containers/Complaint/Complaint.schemas';
import Menu from 'mk2/containers/Menu/Menu';
import { url } from 'mk2/helpers/urls';
import { UserEntity } from 'mk2/schemas';
import { interpolate } from 'mk2/services/i18n';
import React from 'react';
import { matchPath } from 'react-router';
import styles from './CommentMenu.mscss';

interface OwnProps {
    className?: string;
    comment: CommentEntity;
    inGroups: boolean;
    role: 'author' | 'moderator' | 'admin' | null;
    requestUser: UserEntity;
    location: Location;

    hasReport: boolean;
    hasEdit: boolean;
    hasDelete: boolean;
    hasMoveUp: boolean;

    onToggleMenuMore(commentId: number);
    onReport(entityType: ComplaintTargetType, commentId: number);
    onDelete(commentId: number);
    onMoveUp?(commentId: number);
    onSendHeart?();
}

type Props = OwnProps;

class CommentMenu extends React.PureComponent<Props> {
    public render() {
        const {
            className,
            comment,
            inGroups,
            role,
            location,
            hasReport,
            hasEdit,
            hasDelete,
            hasMoveUp,
            requestUser,
            onSendHeart,
        } = this.props;

        const showReport = hasReport && !comment.deletedAt && !comment.byAdmin && ACTIVE_SERVER_ID !== 207;
        const showEdit = hasEdit && !comment.deletedAt;
        const showDelete = hasDelete && !comment.deletedAt;
        const showMoveUp = hasMoveUp && !comment.deletedAt;
        const showSendHeart = !comment.byAdmin && ACTIVE_SERVER_ID !== 207 && !isAnonymousUser(comment.authorUser);

        const match = matchPath<{
            topicSlug?: string;
            postSlug?: string;
        }>(location.pathname, { path: forumPostDetailUrl, exact: true });

        const inForumPostDetail = match && match.params.topicSlug && match.params.postSlug;
        const showWhoLiked = comment.likeable.likesCount > 0 && isAuthenticated(requestUser);

        return isAuthenticated(requestUser) && (showReport || showEdit || showDelete || showMoveUp) ? (
            <Menu
                className={cx(styles.CommentMenu, className)}
                menuId={
                    inGroups
                        ? BLOG_POST_COMMENT_MENU_MORE(comment.id)
                        : FORUM_COMMENT_MENU_MORE(comment.id)
                }
                trigger={
                    <PostMenuTrigger
                        className={cx(inGroups && styles['CommentMenu__trigger--inGroups'])}
                        onClick={this.handleOnToggleMenuMore}
                    />
                }
                triggerShadow={false}
            >
                {showReport && (
                    <MenuItem
                        label={AUTOMATIC_COMMENT_VIOLATES_RULES}
                        icon="fa-exclamation-triangle"
                        onClick={this.handleOnReport}
                    />
                )}

                {showSendHeart && (
                    <MenuItem
                        label={interpolate(SEND_HEART_TO_username, {
                            username: `${comment.authorUser.username}`,
                        })}
                        icon="fa-heart-o"
                        link={
                            isAuthenticated(requestUser)
                                ? url(heartsUrl, {}, { to: comment.authorUser.username, next: location.pathname })
                                : null
                        }
                        onClick={isAuthenticated(requestUser) ? null : onSendHeart}
                        asAnchor={isAuthenticated(requestUser)}
                    />
                )}

                {showWhoLiked && (
                    <MenuItem
                        label={SHOW_USERS_WHO_LIKED_THIS_POST}
                        icon="fa-thumbs-o-up"
                        link={
                            inForumPostDetail
                                ? url(forumPostCommentLikersUrl, {
                                    topicSlug: match.params.topicSlug,
                                    postSlug: match.params.postSlug,
                                    commentId: comment.id,
                                  })
                                : inGroups
                                ? url(groupsEntityLikersUrl, { type: 'comment', entityId: comment.id })
                                : null
                        }
                    />
                )}

                {showEdit && (
                    <MenuItem
                        className={styles[`CommentMenu__item--${role}`]}
                        label={FORUM_COMMENT_EDIT_MENU}
                        icon="fa-pencil"
                        link={url(forumCommentEditUrl, { commentId: comment.id })}
                    />
                )}

                {showDelete && (
                    <MenuItem
                        className={styles[`CommentMenu__item--${role}`]}
                        label={DELETE}
                        icon="fa-trash"
                        onClick={this.handleOnDelete}
                    />
                )}
                {showMoveUp && (
                    <MenuItem
                        className={styles[`Comment__dropdownMenu__item--${role}`]}
                        label={FORUM_COMMENT_MOVE_UP}
                        icon="fa-pencil"
                        onClick={this.handleOnMoveUp}
                    />
                )}
            </Menu>
        ) : null;
    }

    private handleOnToggleMenuMore = () => {
        const { comment, onToggleMenuMore } = this.props;
        onToggleMenuMore(comment.id);
    };

    private handleOnReport = () => {
        const { comment, inGroups, onReport } = this.props;
        if (inGroups) {
            onReport(ComplaintTargetType.PhotoblogComment, comment.id);
        } else {
            onReport(ComplaintTargetType.ForumMessage, comment.id);
        }
    };

    private handleOnDelete = () => {
        const { comment, onDelete } = this.props;
        onDelete(comment.id);
    };

    private handleOnMoveUp = () => {
        const { comment, onMoveUp } = this.props;
        onMoveUp(comment.id);
    };
}

export default CommentMenu;
